<script setup>
import { computed, ref, watch } from 'vue';
import Draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  column: {
    // { title: string, color: string }  -> uma etiqueta (label) do Chatwoot
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['moveConversation']);

const columnLabel = computed(() => props.column.title);

// vuedraggable (SortableJS) precisa de um array local mutável por coluna
// para poder mover itens entre listas via drag and drop. Mantemos essa
// cópia sincronizada com a prop `conversations` sempre que ela mudar
// (ex.: após um fetch), sem nunca mutar a prop diretamente.
const localItems = ref([...props.conversations]);

watch(
  () => props.conversations,
  newValue => {
    localItems.value = [...newValue];
  }
);

// A troca de label só é persistida quando um card é efetivamente solto
// (evento "added") dentro desta coluna — evita chamadas de API durante
// reordenações puramente visuais.
const onChange = event => {
  if (event?.added) {
    const { element } = event.added;
    emit('moveConversation', {
      conversation: element,
      newColumnValue: props.column.title,
    });
  }
};
</script>

<template>
  <div class="flex flex-col flex-shrink-0 w-72 h-full">
    <div class="flex items-center gap-2 px-2 py-2 flex-shrink-0">
      <span
        class="size-2 rounded-full flex-shrink-0"
        :style="{ backgroundColor: column.color }"
      />
      <h3 class="text-sm font-medium text-n-slate-12 m-0 truncate">
        {{ columnLabel }}
      </h3>
      <span class="text-xs text-n-slate-10">{{ conversations.length }}</span>
    </div>
    <div
      class="flex-grow overflow-y-auto rounded-lg bg-n-alpha-1 p-2 min-h-[120px]"
    >
      <div v-if="isFetching" class="flex justify-center py-6">
        <Spinner />
      </div>
      <Draggable
        v-else
        :list="localItems"
        item-key="id"
        group="kanban-conversations"
        class="flex flex-col gap-2 min-h-[80px]"
        ghost-class="opacity-40"
        @change="onChange"
      >
        <template #item="{ element }">
          <KanbanCard :conversation="element" />
        </template>
      </Draggable>
    </div>
  </div>
</template>
